import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Table, Badge, Button, Spinner, Modal, Form, Alert } from 'react-bootstrap';
import { LuArrowLeft, LuCheck, LuX, LuPackage, LuTruck, LuUser, LuMapPin, LuFileText, LuClock } from "react-icons/lu";
import { getOrderById, acceptOrder, rejectOrder, markProcessing } from '../../redux/reducers/orderSlice';
import { updateShipmentStatus } from '../../redux/reducers/shipmentSlice';
import moment from 'moment';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { formatCurrency } from '../../utils/currency';

const OrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentOrder, loading } = useSelector((state) => state.order);
    const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');

    const [rejectModalShow, setRejectModalShow] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    useEffect(() => {
        dispatch(getOrderById(id));
    }, [dispatch, id]);

    const handleAccept = () => dispatch(acceptOrder(id));
    const handleProcessing = () => dispatch(markProcessing(id));
    const handleReject = () => {
        dispatch(rejectOrder({ id, rejectionReason }));
        setRejectModalShow(false);
    };

    const handleMarkDelivered = () => {
        if (currentOrder?.shipmentId?._id || currentOrder?.shipmentId) {
            const shipmentId = currentOrder.shipmentId?._id || currentOrder.shipmentId;
            dispatch(updateShipmentStatus({ 
                id: shipmentId, 
                statusData: { 
                    status: 'DELIVERED', 
                    note: 'Marked as delivered from order management' 
                } 
            })).then(() => {
                dispatch(getOrderById(id));
            });
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case "PAID":
            case "DELIVERED":
                return <Badge bg="success-subtle" className="text-success rounded-pill px-3 py-1 fw-bold">{status === 'PAID' ? 'Paid' : 'Delivered'}</Badge>;
            case "PENDING_PAYMENT":
                return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3 py-1 fw-bold">Pending Payment</Badge>;
            case "ACCEPTED":
                return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1 fw-bold">Accepted</Badge>;
            case "PROCESSING":
                return <Badge bg="primary-subtle" className="text-primary rounded-pill px-3 py-1 fw-bold">Processing</Badge>;
            case "SHIPPED":
                return <Badge bg="info-subtle" className="text-info rounded-pill px-3 py-1 fw-bold">Shipped</Badge>;
            case "CANCELLED":
                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1 fw-bold">Cancelled</Badge>;
            case "REJECTED":
                return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3 py-1 fw-bold">Rejected</Badge>;
            default:
                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3 py-1 fw-bold">{status?.replace('_', ' ')}</Badge>;
        }
    };

    if (loading && !currentOrder) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;
    if (!currentOrder) return <div className="text-center py-5">Order not found.</div>;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <Button variant="light" onClick={() => navigate('/orders')} className="rounded-3 p-2">
                        <LuArrowLeft size={20} />
                    </Button>
                    <div>
                        <h4 className="fw-bold text-dark mb-0">Order {currentOrder.orderNumber}</h4>
                        <div className="text-muted small">Customer: {currentOrder.customerId?.name}</div>
                    </div>
                </div>
                {getStatusBadge(currentOrder.status)}
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                        <Card.Header className="bg-white border-0 p-4">
                            <h6 className="fw-bold mb-0">Order Items</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4 py-3 text-muted small fw-bold">ITEM</th>
                                        <th className="py-3 text-muted small fw-bold text-center">QTY</th>
                                        <th className="py-3 text-muted small fw-bold text-end">PRICE</th>
                                        <th className="pe-4 py-3 text-muted small fw-bold text-end">TOTAL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentOrder.items?.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="rounded-3 bg-light d-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                                                        {item.productImage ? <img src={item.productImage} alt="" className="rounded-3 w-100 h-100 object-fit-cover" /> : <LuPackage className="text-muted opacity-50" size={20} />}
                                                    </div>
                                                    <div className="fw-semibold text-dark small">{item.productName}</div>
                                                </div>
                                            </td>
                                            <td className="py-3 text-center small">{item.quantity}</td>
                                            <td className="py-3 text-end text-muted small">{formatCurrency(item.unitPrice, currency)}</td>
                                            <td className="pe-4 py-3 text-end fw-bold small">{formatCurrency(item.lineTotal, currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                        <Card.Footer className="bg-white border-0 p-4 border-top">
                            <Row className="justify-content-end">
                                <Col md={5}>
                                    <div className="d-flex justify-content-between mb-2 text-muted small">
                                        <span>Subtotal</span>
                                        <span>{formatCurrency(currentOrder.totals?.subtotal, currency)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2 text-muted small">
                                        <span>Tax</span>
                                        <span>{formatCurrency(currentOrder.totals?.taxAmount, currency)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-3 text-muted small">
                                        <span>Shipping</span>
                                        <span className="text-success fw-semibold">{formatCurrency(currentOrder.totals?.shippingEstimate, currency)}</span>
                                    </div>
                                    <div className="d-flex justify-content-between pt-2 border-top">
                                        <h5 className="fw-bold">Total</h5>
                                        <h5 className="fw-bold" style={{ color: "#28B0C4" }}>{formatCurrency(currentOrder.totals?.total, currency)}</h5>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Footer>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuMapPin size={24} style={{ color: "#28B0C4" }} />
                                <h6 className="fw-bold mb-0">Delivery & Customer Info</h6>
                            </div>
                            <Row>
                                <Col md={6}>
                                    <div className="bg-light rounded-4 p-4 h-100">
                                        <div className="text-muted extra-small fw-bold text-uppercase mb-2">Recipient</div>
                                        <div className="fw-bold text-dark">{currentOrder.deliveryAddress?.recipientName}</div>
                                        <div className="text-muted small mt-1">
                                            {currentOrder.deliveryAddress?.street}, {currentOrder.deliveryAddress?.city}<br />
                                            {currentOrder.deliveryAddress?.state} {currentOrder.deliveryAddress?.zipCode}, {currentOrder.deliveryAddress?.country}
                                        </div>
                                        <div className="text-muted small mt-2">{formatPhoneNumber(currentOrder.deliveryAddress?.phone)}</div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="bg-light rounded-4 p-4 h-100">
                                        <div className="text-muted extra-small fw-bold text-uppercase mb-2">Customer Account</div>
                                        <div className="fw-bold text-dark">{currentOrder.customerId?.name}</div>
                                        <div className="text-muted small mt-1">{currentOrder.customerId?.email}</div>
                                        <div className="text-muted small mt-1">{formatPhoneNumber(currentOrder.customerId?.phone)}</div>
                                        <Button as={Link} to={`/customer-detail/${currentOrder.customerId?._id}`} variant="link" className="p-0 mt-2 text-decoration-none small fw-bold" style={{ color: "#28B0C4" }}>View Profile</Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-4">Manage Order</h6>
                            
                            {currentOrder.status === 'PAID' && (
                                <div className="d-grid gap-2">
                                    <Button onClick={handleAccept} className="rounded-3 py-2 fw-bold text-white border-0" style={{ background: "#28B0C4" }}>
                                        Accept Order
                                    </Button>
                                    <Button onClick={() => setRejectModalShow(true)} variant="outline-danger" className="rounded-3 py-2">
                                        Reject & Refund
                                    </Button>
                                </div>
                            )}

                            {currentOrder.status === 'ACCEPTED' && (
                                <div className="d-grid gap-2">
                                    <Button onClick={handleProcessing} className="rounded-3 py-2 fw-bold text-white border-0" style={{ background: "#28B0C4" }}>
                                        Start Processing
                                    </Button>
                                </div>
                            )}

                            {(currentOrder.status === 'PROCESSING' || currentOrder.status === 'SHIPPED') && (
                                <div className="d-grid gap-2">
                                    {currentOrder.status === 'PROCESSING' && !currentOrder.shipmentId ? (
                                        <Button as={Link} to={`/view-invoice/${currentOrder.invoiceId?._id || currentOrder.invoiceId}`} className="rounded-3 py-2 fw-bold text-white border-0" style={{ background: "#28B0C4" }}>
                                            Create Shipment
                                        </Button>
                                    ) : currentOrder.shipmentId ? (
                                        <Button onClick={handleMarkDelivered} className="rounded-3 py-2 fw-bold text-white border-0" style={{ background: "#10b981" }}>
                                            <LuCheck className="me-2" /> Mark as Delivered
                                        </Button>
                                    ) : null}
                                </div>
                            )}

                            {!['PAID', 'ACCEPTED', 'PROCESSING', 'SHIPPED'].includes(currentOrder.status) && (
                                <div className="text-center py-2 text-muted small">
                                    No immediate actions available for this status.
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 mb-4">
                                <LuFileText size={24} style={{ color: "#28B0C4" }} />
                                <h6 className="fw-bold mb-0">Related Invoice</h6>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="small fw-bold text-dark">#{currentOrder.invoiceId?._id?.slice(-8).toUpperCase()}</div>
                                <Badge bg={currentOrder.invoiceId?.status?.toLowerCase() === 'paid' ? 'success' : 'warning'} className="rounded-pill">
                                    {currentOrder.invoiceId?.status || 'Sent'}
                                </Badge>
                            </div>
                            <Button as={Link} to={`/view-invoice/${currentOrder.invoiceId?._id || currentOrder.invoiceId}`} variant="light" className="w-100 rounded-3 py-2 text-dark small">View Invoice</Button>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-4">Order Timeline</h6>
                            <div className="timeline-v2">
                                {currentOrder.timeline?.slice().reverse().map((step, idx) => (
                                    <div key={idx} className="timeline-item-v2 position-relative ps-4 pb-4">
                                        <div className="timeline-point position-absolute start-0 rounded-circle bg-white border-2" style={{ width: '12px', height: '12px', borderColor: idx === 0 ? "#28B0C4" : "#e2e8f0", marginTop: '6px' }}></div>
                                        <div className={`fw-bold small ${idx === 0 ? 'text-dark' : 'text-muted'}`}>{step.status.replace('_', ' ')}</div>
                                        <div className="text-muted extra-small">{step.note}</div>
                                        <div className="text-muted extra-small opacity-75">{moment(step.timestamp).fromNow()}</div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={rejectModalShow} onHide={() => setRejectModalShow(false)} centered>
                <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold">Reject Order</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" className="small border-0 rounded-3">
                        Rejecting a PAID order will initiate a full refund through Stripe.
                    </Alert>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Reason for Rejection</Form.Label>
                        <Form.Control as="textarea" rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="e.g. Out of stock, delivery unavailable..." className="rounded-3" />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setRejectModalShow(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleReject} disabled={!rejectionReason}>Confirm Rejection & Refund</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default OrderDetail;
